import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AppTopBar, Breadcrumbs } from '../components/AppTopBar.js';
import { ApiError, apiFetch } from '../lib/api.js';
import { t, formatDateTime } from '../lib/i18n.js';

/**
 * Executive 360 (spec §13.8): the three applications at a glance.
 *
 * Every figure links into the application that owns it — this is a view, not a
 * hub, and nothing is acted on here (ADR-0001). Every figure is counted within
 * the viewer's own scope in that application.
 */

interface Kpi {
  label: string;
  value: number | null;
  attention?: boolean;
  link: string;
}

interface ExecutiveSummary {
  generatedAt: string;
  apps: { app: string; label: string; kpis: Kpi[] }[];
  escalations: { app: string; label: string; breached: number; escalated: number; link: string }[];
  operations: {
    connectors: { type: string; name: string; enabled: boolean; health: string }[];
    undeliveredEmail: number;
  } | null;
}

const fetchSummary = () => apiFetch<ExecutiveSummary>('/executive/summary');

const REPORTS = [
  { key: 'exec.adoption', label: 'Usage and adoption' },
  { key: 'exec.ai_usage', label: 'AI usage and governance' },
  { key: 'exec.integration_health', label: 'Integration and job health' },
];

export function Executive() {
  const summary = useQuery({ queryKey: ['executive'], queryFn: fetchSummary, retry: false, staleTime: 0 });

  return (
    <div className="min-h-full">
      <AppTopBar app="executive" />
      <Breadcrumbs app="executive" trail={[]} />

      <main className="mx-auto max-w-6xl px-4 pb-16 pt-4">
        <h1 className="m-0 text-2xl font-semibold">{t("Executive 360")}</h1>
        <p className="mt-1 text-sm text-content-secondary">
          {t("All three applications at a glance, within what your role can see. Each figure opens the application behind it.")}
        </p>

        {summary.isLoading && <p className="mt-6 text-content-secondary">{t("Loading…")}</p>}
        {summary.error && (
          <section className="mt-6 rounded-md border border-border bg-tone-info-surface p-6" role="status">
            <h2 className="m-0 text-base font-semibold">{t("Not available to your role")}</h2>
            <p className="mb-0 mt-2 text-sm text-content-secondary">
              {summary.error instanceof ApiError
                ? summary.error.message
                : t("The Executive 360 view is for access administrators and people who can view all records in all three applications.")}
            </p>
          </section>
        )}

        {summary.data && (
          <>
            <div className="mt-6 grid gap-4 md:grid-cols-3">
              {summary.data.apps.map((app) => (
                <section key={app.app} className="rounded-md border border-border p-4" aria-labelledby={`kpis-${app.app}`}>
                  <h2 id={`kpis-${app.app}`} className="m-0 text-base font-semibold">
                    {t(app.label)}
                  </h2>
                  <ul className="m-0 mt-3 list-none space-y-2 p-0">
                    {app.kpis.map((kpi) => (
                      <li key={t(kpi.label)} data-testid="kpi">
                        <Link to={kpi.link} className="flex items-baseline justify-between gap-3 no-underline">
                          <span className="text-sm text-content-secondary">{t(kpi.label)}</span>
                          <strong
                            className="text-lg"
                            style={kpi.attention ? { color: 'var(--tone-critical)' } : undefined}
                          >
                            {kpi.value === null ? '—' : kpi.value}
                          </strong>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>

            <section className="mt-8" aria-labelledby="escalations-heading">
              <h2 id="escalations-heading" className="m-0 text-lg font-semibold">
                {t("Open escalations")}
              </h2>
              <table className="mt-3 w-full border-collapse text-sm">
                <thead>
                  <tr className="text-content-secondary">
                    <th className="border-b border-border p-2 text-start font-normal">{t("Application")}</th>
                    <th className="border-b border-border p-2 text-end font-normal">{t("Breached")}</th>
                    <th className="border-b border-border p-2 text-end font-normal">{t("Escalated")}</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.data.escalations.map((row) => (
                    <tr key={row.app}>
                      <td className="border-b border-border p-2">
                        <Link to={row.link} className="text-ag-purple">
                          {t(row.label)}
                        </Link>
                      </td>
                      <td className="border-b border-border p-2 text-end">{row.breached}</td>
                      <td className="border-b border-border p-2 text-end">{row.escalated}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>

            {summary.data.operations && (
              <section className="mt-8 rounded-md border border-border p-4 text-sm" data-testid="operations">
                <h2 className="m-0 text-lg font-semibold">{t("Connections")}</h2>
                {summary.data.operations.connectors.length === 0 ? (
                  <p className="mb-0 mt-2 text-content-secondary">{t("No connection has been set up.")}</p>
                ) : (
                  <ul className="m-0 mt-2 list-none space-y-1 p-0">
                    {summary.data.operations.connectors.map((connector) => (
                      <li key={connector.type}>
                        {connector.name}: {connector.enabled ? connector.health : t("off")}
                      </li>
                    ))}
                  </ul>
                )}
                {summary.data.operations.undeliveredEmail > 0 && (
                  <p className="mb-0 mt-2">
                    {summary.data.operations.undeliveredEmail}{' '}{t("email(s) not sent or failed.")}{' '}
                    <Link to="/qaqc/settings/integrations" className="text-ag-purple">
                      {t("See the outbox")}
                    </Link>
                  </p>
                )}
              </section>
            )}

            <section className="mt-8" aria-labelledby="exec-reports-heading">
              <h2 id="exec-reports-heading" className="m-0 text-lg font-semibold">
                {t("Across the applications")}
              </h2>
              <ul className="m-0 mt-3 list-none space-y-2 p-0">
                {REPORTS.map((report) => (
                  <li key={report.key}>
                    <Link to={`/executive/reports?report=${report.key}`} className="text-ag-purple">
                      {t(report.label)}
                    </Link>
                  </li>
                ))}
              </ul>
            </section>

            <p className="mt-8 text-xs text-content-secondary">{t("As of")}{' '}{formatDateTime(summary.data.generatedAt)}.</p>
          </>
        )}
      </main>
    </div>
  );
}
